import { useState, useCallback, useRef } from 'react';
import { uploadCsv, uploadVideo, uploadXrk, uploadMotec, uploadSmartycam3, getLapTrace, getTheoreticalBest, loadSession as apiLoadSession } from '../services/api';

// Sentinel lap index for the synthetic Theoretical Best lap.
export const TB_LAP_INDEX = -1;

export function useSession() {
  const [session, setSession] = useState(null);     // upload response
  const [loading, setLoading] = useState(false);    // false | true | 'video' | 'xrk'
  const [error, setError] = useState(null);
  const [duplicateInfo, setDuplicateInfo] = useState(null); // { existingSessionId, existingSessionName } | null
  const [lapTraces, setLapTraces] = useState({});   // { lapIndex: traceData } — TB stored at TB_LAP_INDEX
  const [loadingTraces, setLoadingTraces] = useState(new Set());
  const [tbMeta, setTbMeta] = useState(null);       // { segments, total_time_s, source_lap_count }
  const [uploadProgress, setUploadProgress] = useState(null); // null | 0-100 during upload

  // Stores the pending file+kind so forceUpload can retry it
  const pendingUploadRef = useRef(null);

  const upload = useCallback(async (file, force = false) => {
    setLoading(true);
    setError(null);
    setDuplicateInfo(null);
    setUploadProgress(0);
    try {
      const data = await uploadCsv(file, pct => pct >= 100 ? setUploadProgress(null) : setUploadProgress(pct), { force });
      // A multi-fragment RaceChrono file produces several sessions from one
      // upload (see additional_session_ids) — don't navigate into any of
      // them, just let the caller refresh the session list.
      if (!data.additional_session_ids?.length) {
        setSession(data);
        setLapTraces({});
        setTbMeta(null);
      }
      return data;
    } catch (e) {
      if (e.isDuplicate) {
        pendingUploadRef.current = { file, kind: 'csv' };
        setDuplicateInfo({ existingSessionId: e.existingSessionId, existingSessionName: e.existingSessionName });
      } else {
        setError(e.message);
      }
      return null;
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  }, []);

  const uploadMov = useCallback(async (file, force = false) => {
    setLoading('video');
    setError(null);
    setDuplicateInfo(null);
    setUploadProgress(0);
    try {
      const data = await uploadVideo(file, pct => pct >= 100 ? setUploadProgress(null) : setUploadProgress(pct), { force });
      setSession(data);
      setLapTraces({});
      setTbMeta(null);
      return data;
    } catch (e) {
      if (e.isDuplicate) {
        pendingUploadRef.current = { file, kind: 'video' };
        setDuplicateInfo({ existingSessionId: e.existingSessionId, existingSessionName: e.existingSessionName });
      } else {
        setError(e.message);
      }
      return null;
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  }, []);

  const fetchTrace = useCallback(async (lapIndex) => {
    if (!session) return null;
    if (lapTraces[lapIndex]) return lapTraces[lapIndex];
    if (loadingTraces.has(lapIndex)) return null;

    setLoadingTraces(prev => new Set([...prev, lapIndex]));
    try {
      const trace = await getLapTrace(session.session_id, lapIndex);
      setLapTraces(prev => ({ ...prev, [lapIndex]: trace }));
      return trace;
    } catch (e) {
      console.error('Failed to fetch trace', lapIndex, e);
      return null;
    } finally {
      setLoadingTraces(prev => {
        const next = new Set(prev);
        next.delete(lapIndex);
        return next;
      });
    }
  }, [session, lapTraces, loadingTraces]);

  const uploadXrkFile = useCallback(async (file, force = false) => {
    setLoading('xrk');
    setError(null);
    setDuplicateInfo(null);
    setUploadProgress(0);
    try {
      const data = await uploadXrk(file, pct => pct >= 100 ? setUploadProgress(null) : setUploadProgress(pct), { force });
      setSession(data);
      setLapTraces({});
      setTbMeta(null);
      return data;
    } catch (e) {
      if (e.isDuplicate) {
        pendingUploadRef.current = { file, kind: 'xrk' };
        setDuplicateInfo({ existingSessionId: e.existingSessionId, existingSessionName: e.existingSessionName });
      } else {
        setError(e.message);
      }
      return null;
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  }, []);

  const uploadSmartycam3File = useCallback(async (file, force = false) => {
    setLoading('smartycam3');
    setError(null);
    setDuplicateInfo(null);
    setUploadProgress(0);
    try {
      const data = await uploadSmartycam3(file, pct => pct >= 100 ? setUploadProgress(null) : setUploadProgress(pct), { force });
      setSession(data);
      setLapTraces({});
      setTbMeta(null);
      return data;
    } catch (e) {
      if (e.isDuplicate) {
        pendingUploadRef.current = { file, kind: 'smartycam3' };
        setDuplicateInfo({ existingSessionId: e.existingSessionId, existingSessionName: e.existingSessionName });
      } else {
        setError(e.message);
      }
      return null;
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  }, []);

  const uploadMotecFile = useCallback(async (file, sidecar = null, force = false) => {
    setLoading('motec');
    setError(null);
    setDuplicateInfo(null);
    setUploadProgress(0);
    try {
      const data = await uploadMotec(file, pct => pct >= 100 ? setUploadProgress(null) : setUploadProgress(pct), { force, sidecar });
      setSession(data);
      setLapTraces({});
      setTbMeta(null);
      return data;
    } catch (e) {
      if (e.isDuplicate) {
        pendingUploadRef.current = { file, kind: 'motec', sidecar };
        setDuplicateInfo({ existingSessionId: e.existingSessionId, existingSessionName: e.existingSessionName });
      } else {
        setError(e.message);
      }
      return null;
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  }, []);

  const forceUpload = useCallback(async () => {
    const pending = pendingUploadRef.current;
    if (!pending) return null;
    pendingUploadRef.current = null;
    setDuplicateInfo(null);

    const { file, kind, sidecar } = pending;
    const loadingLabel = kind === 'video' ? 'video' : kind === 'xrk' ? 'xrk' : kind === 'motec' ? 'motec' : kind === 'smartycam3' ? 'smartycam3' : true;
    setLoading(loadingLabel);
    setError(null);
    setUploadProgress(0);
    try {
      let apiFn;
      if (kind === 'csv') apiFn = uploadCsv;
      else if (kind === 'video') apiFn = uploadVideo;
      else if (kind === 'motec') apiFn = uploadMotec;
      else if (kind === 'smartycam3') apiFn = uploadSmartycam3;
      else apiFn = uploadXrk;

      const opts = kind === 'motec' ? { force: true, sidecar } : { force: true };
      const data = await apiFn(file, pct => pct >= 100 ? setUploadProgress(null) : setUploadProgress(pct), opts);
      setSession(data);
      setLapTraces({});
      setTbMeta(null);
      return data;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  }, []);

  const clearDuplicate = useCallback(() => {
    setDuplicateInfo(null);
    pendingUploadRef.current = null;
  }, []);

  const loadSession = useCallback(async (sessionId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiLoadSession(sessionId);
      setSession(data);
      setLapTraces({});
      setTbMeta(null);
      return data;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const resetSession = useCallback(() => {
    setSession(null);
    setLapTraces({});
    setTbMeta(null);
  }, []);

  // Compute / refresh the Theoretical Best for the current session against
  // the supplied corner set. Stores the TB trace under TB_LAP_INDEX in lapTraces
  // and the segment metadata in tbMeta. Pass null/[] to clear.
  const fetchTb = useCallback(async (sectors) => {
    if (!session?.session_id) return null;
    if (!sectors || sectors.length === 0) {
      setTbMeta(null);
      setLapTraces(prev => {
        if (prev[TB_LAP_INDEX] === undefined) return prev;
        const next = { ...prev };
        delete next[TB_LAP_INDEX];
        return next;
      });
      return null;
    }
    setLoadingTraces(prev => new Set([...prev, TB_LAP_INDEX]));
    try {
      const { tb_meta, trace } = await getTheoreticalBest(session.session_id, sectors);
      setTbMeta(tb_meta);
      setLapTraces(prev => ({ ...prev, [TB_LAP_INDEX]: trace }));
      return { tb_meta, trace };
    } catch (e) {
      console.error('Failed to compute theoretical best', e);
      setTbMeta(null);
      setLapTraces(prev => {
        if (prev[TB_LAP_INDEX] === undefined) return prev;
        const next = { ...prev };
        delete next[TB_LAP_INDEX];
        return next;
      });
      return null;
    } finally {
      setLoadingTraces(prev => {
        const next = new Set(prev);
        next.delete(TB_LAP_INDEX);
        return next;
      });
    }
  }, [session]);

  const sessionId = session?.session_id ?? null;
  return {
    session, setSession, sessionId, loading, error, uploadProgress,
    duplicateInfo, forceUpload, clearDuplicate,
    lapTraces, loadingTraces,
    tbMeta, fetchTb,
    upload, uploadMov, uploadXrk: uploadXrkFile, uploadMotec: uploadMotecFile, uploadSmartycam3: uploadSmartycam3File, loadSession, fetchTrace, resetSession,
  };
}
